// NBA-style apron logic, layered onto ZenGM's soft cap.
//
// ZenGM models a single tax line + a flat luxury tax + one trade salary-match rule. The real NBA
// (2023 CBA) has FOUR payroll tiers — cap -> tax line -> first apron -> second apron — each
// tightening trade/roster rules, plus a progressive + repeater luxury tax. This module is the
// single source of truth for the tiers; the stateful penalties (repeater tax, and later frozen
// picks) read a per-season "apron ledger" stored on teamSeason.
//
// LEAN v1 scope: tiers + tiered trade matching (2nd apron = 100% take-back) + progressive/repeater
// tax + AI avoidance (teams are "allergic" to the 2nd apron, treating it ~like a hard cap).
// Deferred: frozen first-round picks, mid-level/sign-and-trade bans (no analog in ZenGM yet).
//
// All amounts are in THOUSANDS of dollars, matching g.get("salaryCap") / getPayroll().

import { g } from "../../util/index.ts";
import { idb } from "../../db/index.ts";

// Feature flag — promotable to a real gameAttribute later. Apron rules only apply to soft-cap
// leagues (a hard cap already forbids going over; "none" has no cap).
export const APRON_LOGIC = true;

export const APRON_CONFIG = {
	// Apron thresholds ABOVE the tax line, as a fraction of the salary cap (no new gameAttributes
	// for v1 — derived from the existing luxuryPayroll + salaryCap). Shape from NBA 2024-25:
	// 1st apron ~ tax + 5% of cap, 2nd apron ~ tax + 13% of cap.
	firstApronOverTaxFrac: 0.05,
	secondApronOverTaxFrac: 0.13,

	// Trade salary-match take-back %, indexed by apron level (0..3). Below the 2nd apron this
	// keeps ZenGM's existing over-cap rule (softCapTradeSalaryMatch, default 125). At the 2nd
	// apron it drops to 100 — you can't take back more salary than you send out.
	tradeSalaryMatchByLevel: [undefined, undefined, undefined, 100] as (
		| number
		| undefined
	)[],

	// Progressive luxury tax: bracket width as a fraction of the cap (~$5M at a $150M cap), the
	// multiplier per successive bracket, and the increment applied to every bracket beyond the
	// last listed one.
	taxBracketWidthFrac: 0.033,
	taxRates: [1.5, 1.75, 2.5, 3.25],
	taxRateIncrementPerExtraBracket: 0.5,

	// Repeater tax: +this to every bracket's multiplier for a team that paid tax in at least
	// `repeaterThreshold` of the previous `repeaterWindow` seasons.
	repeaterSurchargePerBracket: 1.0,
	repeaterWindow: 4,
	repeaterThreshold: 3,

	// Frozen picks: a team over the 2nd apron has its own first-round pick this many seasons out
	// frozen (untradeable) — matching the real NBA's 7-years-out rule. Real-players leagues
	// already set numSeasonsFutureDraftPicks = 7 so that pick exists; random leagues default to
	// fewer, so freezeApronPicks clamps to the furthest available own 1st-rounder.
	freezePickOffset: 7,
};

// Whether apron rules are active for the current league (soft cap + flag on).
export const apronsActive = () =>
	APRON_LOGIC && g.get("salaryCapType") === "soft";

// The three thresholds (in thousands): tax line, first apron, second apron.
export const apronThresholds = () => {
	const cap = g.get("salaryCap");
	const tax = g.get("luxuryPayroll");
	return {
		tax,
		first: tax + APRON_CONFIG.firstApronOverTaxFrac * cap,
		second: tax + APRON_CONFIG.secondApronOverTaxFrac * cap,
	};
};

// Payroll -> apron level: 0 = at/below tax line, 1 = over tax, 2 = over 1st apron, 3 = over 2nd
// apron. Always 0 when apron rules are inactive.
export const apronLevel = (payroll: number): 0 | 1 | 2 | 3 => {
	if (!apronsActive()) {
		return 0;
	}
	const t = apronThresholds();
	if (payroll > t.second) {
		return 3;
	}
	if (payroll > t.first) {
		return 2;
	}
	if (payroll > t.tax) {
		return 1;
	}
	return 0;
};

export const isOverSecondApron = (payroll: number) => apronLevel(payroll) === 3;

// The trade salary-match % that applies to a team at this payroll. Falls back to ZenGM's
// existing softCapTradeSalaryMatch except where the apron tier overrides it (2nd apron -> 100%).
export const tradeSalaryMatchPct = (payroll: number): number => {
	const override = APRON_CONFIG.tradeSalaryMatchByLevel[apronLevel(payroll)];
	return override ?? g.get("softCapTradeSalaryMatch");
};

// The set of tids that are REPEATER taxpayers going into `season` — i.e. paid the luxury tax in
// at least `repeaterThreshold` of the previous `repeaterWindow` seasons, per the apron ledger.
// One query for the whole league (used once per season in assessPayrollMinLuxury). Empty when
// apron rules are inactive or before enough ledger history exists.
export const repeaterTaxpayerTids = async (
	season: number,
): Promise<Set<number>> => {
	const set = new Set<number>();
	if (!apronsActive()) {
		return set;
	}
	const c = APRON_CONFIG;
	const prior = await idb.cache.teamSeasons.indexGetAll(
		"teamSeasonsBySeasonTid",
		[[season - c.repeaterWindow], [season - 1, "Z"]],
	);
	const counts = new Map<number, number>();
	for (const ts of prior) {
		if (ts.apron?.paidTax) {
			counts.set(ts.tid, (counts.get(ts.tid) ?? 0) + 1);
		}
	}
	for (const [tid, n] of counts) {
		if (n >= c.repeaterThreshold) {
			set.add(tid);
		}
	}
	return set;
};

// Progressive luxury tax on `payroll` (thousands). `repeater` adds a surcharge to every bracket.
// Returns 0 below the tax line or when tax doesn't apply (hard cap).
export const progressiveLuxuryTax = (payroll: number, repeater: boolean) => {
	const t = apronThresholds();
	const over = payroll - t.tax;
	if (over <= 0) {
		return 0;
	}
	const c = APRON_CONFIG;
	const width = c.taxBracketWidthFrac * g.get("salaryCap");
	let tax = 0;
	let remaining = over;
	let b = 0;
	while (remaining > 0) {
		const inBracket = Math.min(remaining, width);
		const baseRate =
			b < c.taxRates.length
				? c.taxRates[b]!
				: c.taxRates.at(-1)! +
					(b - (c.taxRates.length - 1)) * c.taxRateIncrementPerExtraBracket;
		const rate = baseRate + (repeater ? c.repeaterSurchargePerBracket : 0);
		tax += rate * inBracket;
		remaining -= inBracket;
		b += 1;
	}
	return tax;
};
